/**
 * voice-v8 — die GANZE Aufnahme erkennen, ohne Modellzug. (26.09.2026 spät,
 * dritte Runde an den Sprechtasten-Befehlen, Tickets 2674/2672)
 *
 * ## Der Grundsatz
 *
 * Kein Sprachbefehl und keine Automatik darf Gesprochenes verlieren. Der
 * endgültige Text kommt IMMER aus der Erkennung der ganzen Aufnahme — wie
 * beim Senden (hochladen, der Server erkennt) —, nie aus der mitlaufenden
 * Live- oder Nachhör-Mitschrift. Die sind nur Auslöser und Anzeige.
 *
 * Bis pre.3347 legte „ins Textfeld" die Live-Mitschrift ins Feld, und
 * „Verwerfen" hob nur sie auf. Stand sie (Ticket 2672) oder hinkte sie nach,
 * fehlte genau das Ende des Diktats.
 *
 * ## Der Weg
 *
 * POST /api/voice/erkennen ist die Route des Sendens (server/voice-command.ts)
 * bis zur fertigen Erkennung — dieselbe Reparatur des Kopfs, dieselbe
 * Erkennung, derselbe Stille-Schnitt, dieselben Wächter, dasselbe Endwort —,
 * nur ohne Modellzug, ohne Verlauf und ohne abgelegte Tondatei.
 *
 * ⚠️ Übergang: Ein Server ohne diese Route (vor dem Neustart) antwortet 404.
 *    Dann erkennt /api/transcribe die ganze Aufnahme wie bis pre.3347, und
 *    das Endwort fällt hier mit derselben Regel weg. Nie fällt ein „ins
 *    Textfeld" auf den Sendeweg zurück — der würde ein Modell antworten
 *    lassen.
 *
 * ## Vierte Runde (26.09.2026): EIN Weg für alle Enden
 *
 * Senden, „ins Textfeld", „Umschreiben" und „Zurückholen" nehmen denselben
 * Hochlade- und Erkennungsweg (streamPtt) — mit denselben drei Versuchen
 * beim Hochladen und, wenn er trotzdem scheitert, derselben Karte „Erneut
 * versuchen" mit der aufbewahrten Aufnahme (lib/feld-auftraege.ts). Auch
 * die Mitschrift reist mit wie beim Senden (`live`): der Server nimmt sie
 * nur, wenn seine Erkennung leer bleibt — vorher legte die Seite sie selbst
 * ins Feld, und ein zweiter Versuch hätte den Text verdoppelt.
 */

import { ohneAusgeloestesEndwort, type EndwortAngabe } from "../../shared/sende-wort.js";
import { streamPtt } from "./ptt.js";

const ERKENNEN_ZIEL = "/api/voice/erkennen";

/**
 * Das Ergebnis. `grund` ist ein kurzer Schlüssel fürs Journal:
 *   nichts    der Server hörte nichts oder verstand nichts
 *   gesperrt  das Konto darf gerade nichts erkennen lassen
 *   stt       die Erkennung selbst scheiterte
 *   http-NNN  der Server antwortete mit einem Fehler
 *   netz      keine Verbindung
 *   leer      keine Antwort mit Text
 *
 * `tonUrl` (Ticket 2618): wo der Server den Ton abgelegt hat — nur, wenn
 * er darum gebeten wurde (`ablegen`, der Teil eines langen Diktats).
 */
export type GanzErkannt = (
  | { ok: true; text: string }
  | { ok: false; grund: string }
) & { tonUrl?: string | null };

interface GanzErkennenDeps {
  strom?: typeof streamPtt;
  holen?: (url: string, init: RequestInit) => Promise<Response>;
}

function grundAus(error: string | undefined): string {
  if (!error) return "leer";
  if (/^http-\d{3}$/.test(error)) return error;
  if (error === "gesperrt") return "gesperrt";
  if (/^stt-failed/.test(error)) return "stt";
  if (/nichts (gehört|verstanden)/i.test(error)) return "nichts";
  return "netz";
}

export async function erkenneGanzeAufnahme(
  ton: Blob,
  mime: string,
  /** Der Befehl, der die Aufnahme beendete — er fällt aus dem Text. */
  endwort: EndwortAngabe | null,
  /** Die Einstellung `sendeWort` — nur für den Übergang (siehe Kopf). */
  einstellung: string,
  deps: GanzErkennenDeps = {},
  /**
   * Die Mitschrift — der Rückfall AM SERVER, wie beim Senden (vierte
   * Runde). Leer bei einem zweiten Versuch, wie beim „Erneut senden".
   */
  live = "",
  /**
   * Ticket 2618: der Teil eines langen Diktats — der Server legt den Ton ab
   * wie beim Senden (auch gesperrt, auch wenn er nichts hört) und nennt die
   * Datei (`tonUrl`).
   */
  ablegen = false,
): Promise<GanzErkannt> {
  const strom = deps.strom ?? streamPtt;
  let text: string | null = null;
  let geprueft = false;
  let tonUrl: string | null = null;
  let ende: { ok: boolean; error?: string } | null = null;
  await strom(
    ton,
    mime,
    {
      onTranscript: (t, url, info) => {
        text = t;
        geprueft = info?.endwortGeprueft === true;
        tonUrl = url ?? tonUrl;
      },
      onTondatei: (url) => {
        tonUrl = url;
      },
      onTextChunk: () => undefined,
      onToolCall: () => undefined,
      onToolResult: () => undefined,
      onDone: (ok, _t, error) => {
        ende = { ok, error };
      },
    },
    live,
    undefined,
    { endwort, ziel: ERKENNEN_ZIEL, ...(ablegen ? { teil: true } : {}) },
  );
  const e = ende as { ok: boolean; error?: string } | null;
  const erkannt = text as string | null;
  const abgelegt = ablegen ? { tonUrl: tonUrl as string | null } : {};
  if (e?.ok && erkannt !== null) {
    return {
      ok: true,
      text: geprueft ? erkannt : ohneAusgeloestesEndwort(erkannt, einstellung, endwort),
      ...abgelegt,
    };
  }
  if (e?.error === "http-404") return erkenneUeberTranscribe(ton, endwort, einstellung, deps);
  return { ok: false, grund: grundAus(e?.error), ...abgelegt };
}

/** Der Übergang bis zum Neustart (siehe Kopf): die ganze Aufnahme über /api/transcribe. */
async function erkenneUeberTranscribe(
  ton: Blob,
  endwort: EndwortAngabe | null,
  einstellung: string,
  deps: GanzErkennenDeps,
): Promise<GanzErkannt> {
  const holen = deps.holen ?? ((url: string, init: RequestInit) => fetch(url, init));
  const fd = new FormData();
  fd.append("audio", ton, ton.type.includes("mp4") ? "ptt.m4a" : "ptt.webm");
  try {
    const r = await holen("/api/transcribe", { method: "POST", credentials: "same-origin", body: fd });
    if (!r.ok) return { ok: false, grund: `http-${r.status}` };
    const j = (await r.json()) as { text?: unknown };
    const roh = typeof j.text === "string" ? j.text.trim() : "";
    return { ok: true, text: ohneAusgeloestesEndwort(roh, einstellung, endwort) };
  } catch {
    return { ok: false, grund: "netz" };
  }
}
